import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  getDesktopUpdateDialogCopy,
  isTaskMantisDesktop,
  rememberDismissedDesktopUpdate,
  shouldSkipAutoDesktopUpdatePrompt,
} from "../desktopUpdate";
import "./DesktopUpdateModal.css";

/**
 * Electron-only non-blocking toast when a newer unsigned DMG is on hosting,
 * or when the user chooses Check for Updates from the menu / settings.
 *
 * @example
 * <DesktopUpdateModal />
 * // Renders nothing in the browser; in Electron shows a bottom toast on update
 */
const DesktopUpdateModal = () => {
  const [payload, setPayload] = useState(null);
  const [installing, setInstalling] = useState(false);
  const canDismiss = Boolean(
    payload && payload.status !== "checking" && payload.status !== "installing"
  );

  const handleDismiss = useCallback(() => {
    setPayload((current) => {
      if (!current || current.status === "checking") return current;
      if (current.status === "available") {
        rememberDismissedDesktopUpdate(current.latestVersion);
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!isTaskMantisDesktop() || !window.starmeshDesktop?.onUpdateStatus) {
      return undefined;
    }

    const applyStatus = (next) => {
      if (!next || typeof next !== "object") return;
      if (next.userInitiated) {
        setPayload(next);
        return;
      }
      if (next.status === "installing" || next.status === "install-ready") {
        // Progress / hand-off ticks only matter while the toast the user clicked
        // Install on is open.
        setPayload((current) => (current ? { ...current, ...next } : current));
        return;
      }
      if (
        next.status === "available" &&
        !shouldSkipAutoDesktopUpdatePrompt(next.latestVersion)
      ) {
        setPayload((current) =>
          current && current.status !== "available" ? current : next
        );
      }
    };

    const stop = window.starmeshDesktop.onUpdateStatus(applyStatus);

    // The launch check can finish before this listener exists; pull the last
    // result from the main process so that push is never lost.
    let cancelled = false;
    window.starmeshDesktop
      .getUpdateStatus?.()
      .then((last) => {
        if (!cancelled) applyStatus(last);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);

  useEffect(() => {
    if (!payload) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [handleDismiss, payload]);

  if (!payload) return null;

  const copy = getDesktopUpdateDialogCopy(payload);

  const handlePrimary = async () => {
    if (!canDismiss) return;
    if (copy.showInstall) {
      const url = payload.downloadUrl || payload.landingUrl;
      if (!url || !window.starmeshDesktop?.installUpdate) return;
      try {
        setInstalling(true);
        await window.starmeshDesktop.installUpdate({
          version: payload.latestVersion,
          downloadUrl: url,
        });
        // Not snoozed on purpose: if the user stops at the Finder Replace
        // prompt, the old build must prompt again on its next launch. The
        // "install-ready" status pushed by the main process stays on screen
        // until the app closes.
      } catch (error) {
        setPayload((current) =>
          current
            ? {
                ...current,
                status: "error",
                error: error?.message || "Could not open the installer",
              }
            : current
        );
      } finally {
        setInstalling(false);
      }
      return;
    }
    handleDismiss();
  };

  return createPortal(
    <div className="desktop-update-toast-region" aria-live="polite">
      <div
        className="desktop-update-toast"
        role="status"
        aria-labelledby="desktop-update-title"
        aria-describedby="desktop-update-desc"
      >
        {canDismiss ? (
          <button
            type="button"
            className="desktop-update-toast__close"
            onClick={handleDismiss}
            aria-label="Dismiss update notice"
          >
            ×
          </button>
        ) : null}
        <div className="desktop-update-toast__copy">
          <p id="desktop-update-title" className="desktop-update-toast__title">
            {copy.title}
          </p>
          <p id="desktop-update-desc" className="desktop-update-toast__desc">
            {copy.description}
          </p>
        </div>
        {canDismiss ? (
          <div className="desktop-update-toast__actions">
            <button
              type="button"
              className="desktop-update-toast__primary"
              onClick={() => void handlePrimary()}
              disabled={installing}
            >
              {installing ? "Opening…" : copy.primaryLabel}
            </button>
            {copy.showInstall ? (
              <button
                type="button"
                className="desktop-update-toast__secondary"
                onClick={handleDismiss}
                disabled={installing}
              >
                Later
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default DesktopUpdateModal;
